import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, ExternalLink, Monitor, Sparkles } from "lucide-react";
import Reveal from "../Reveal";
import { useLanguage } from "../../contexts/LanguageContext";

// Real client websites
const CLIENT_SITES = [
  {
    id: "uiehec",
    title: "UIEHEC",
    url: "https://uiehec.org/",
    domain: "uiehec.org",
    category: "Kurumsal",
    desc: "Uluslararası eğitim ve sağlık konseyi kurumsal web sitesi.",
    color: "from-blue-600 to-cyan-500",
  },
  {
    id: "diomedikal",
    title: "DIO Medikal",
    url: "https://www.diomedikal.com/",
    domain: "diomedikal.com",
    category: "Medikal",
    desc: "Medikal cihaz ve sağlık ekipmanları e-ticaret sitesi.",
    color: "from-teal-600 to-emerald-500",
  },
  {
    id: "puandor",
    title: "Puandorg Müzik",
    url: "https://www.puandorgmuzik.com/",
    domain: "puandorgmuzik.com",
    category: "E-ticaret",
    desc: "Müzik aleti ve aksesuar online mağazası.",
    color: "from-purple-600 to-pink-500",
  },
  {
    id: "softcosmetic",
    title: "Soft Cosmetic",
    url: "https://softcosmetic.com.tr/",
    domain: "softcosmetic.com.tr",
    category: "Güzellik",
    desc: "Kozmetik ürünleri e-ticaret ve marka web sitesi.",
    color: "from-rose-500 to-pink-400",
  },
  {
    id: "alagoz",
    title: "Alaöz Mühendislik",
    url: "https://alagozmuhendislik.com/",
    domain: "alagozmuhendislik.com",
    category: "Mühendislik",
    desc: "Mühendislik ve inşaat sektörü kurumsal tanıtım sitesi.",
    color: "from-orange-600 to-amber-500",
  },
  {
    id: "cskin",
    title: "CS Kinsaat",
    url: "https://www.cskinsaat.com/",
    domain: "cskinsaat.com",
    category: "İnşaat",
    desc: "İnşaat ve yapı firması kurumsal web sitesi.",
    color: "from-slate-600 to-gray-500",
  },
];

function SitePreviewModal({ site, onClose }) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const touchStartX = useRef(null);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-sm flex flex-col items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 24 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-4xl flex flex-col"
        style={{ maxHeight: "90vh" }}
      >
        {/* Browser chrome */}
        <div className="flex items-center gap-2 bg-mist/90 backdrop-blur rounded-t-2xl px-4 py-3 border border-mist border-b-0">
          <div className="flex gap-1.5">
            <span className="h-3 w-3 rounded-full bg-red-400" />
            <span className="h-3 w-3 rounded-full bg-amber-400" />
            <span className="h-3 w-3 rounded-full bg-green-400" />
          </div>
          <div className="flex-1 bg-white rounded-lg px-3 py-1 text-xs text-ink/60 truncate border border-mist">
            🔒 {site.url}
          </div>
          <a href={site.url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-brand-pink hover:text-brand-magenta transition-colors"
            onClick={(e) => e.stopPropagation()}>
            <ExternalLink className="h-3.5 w-3.5" />
            Aç
          </a>
          <button type="button" onClick={onClose}
            className="h-7 w-7 rounded-full bg-ink/10 hover:bg-ink/20 flex items-center justify-center text-ink/60">
            <X className="h-4 w-4" />
          </button>
        </div>
        {/* iframe */}
        <div className="relative flex-1 bg-white rounded-b-2xl overflow-hidden border border-mist border-t-0" style={{ minHeight: 400, maxHeight: "calc(90vh - 52px)" }}>
          {!loaded && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-mist/40">
              <div className="h-8 w-8 rounded-full border-2 border-brand-pink border-t-transparent animate-spin" />
              <p className="text-sm text-ink/60">{site.domain} yükleniyor…</p>
            </div>
          )}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-mist/20 text-center p-8">
              <Monitor className="h-12 w-12 text-ink/30" />
              <p className="text-sm text-ink/60">Bu site iframe önizlemesine izin vermiyor.</p>
              <a href={site.url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-brand-pink text-cream px-5 py-2.5 text-sm font-semibold hover:bg-brand-magenta transition-colors">
                Yeni Sekmede Aç <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          )}
          {!error && (
            <iframe
              src={site.url}
              title={site.title}
              className="w-full h-full"
              style={{ minHeight: 400, border: "none" }}
              onLoad={() => setLoaded(true)}
              onError={() => setError(true)}
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function WebDevPortfolioSection() {
  const { lang } = useLanguage();
  const [preview, setPreview] = useState(null);

  const labels = {
    eyebrow: lang === "tr" ? "REFERANS ÇALIŞMALAR" : "PORTFOLIO",
    title: lang === "tr" ? "Teslim ettiğimiz web siteleri" : "Websites We Delivered",
    subtitle: lang === "tr"
      ? "Her proje müşteriye özel tasarlanmış, hız ve SEO optimize edilmiş web siteleri."
      : "Every project is a custom-designed, speed and SEO-optimized website.",
    previewBtn: lang === "tr" ? "Ön İzle" : "Preview",
    openBtn: lang === "tr" ? "Siteyi Aç" : "Open Site",
  };

  return (
    <section data-testid="webdev-portfolio" className="bg-cream py-section border-t border-mist">
      <div className="container-page">
        <Reveal as="div" className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
          <div>
            <span className="eyebrow">
              <Sparkles className="h-3 w-3 text-brand-pink" />
              {labels.eyebrow}
            </span>
            <h2 className="mt-5 font-display text-display-2 font-semibold text-ink">
              {labels.title}
            </h2>
            <p className="mt-3 text-ink/60 max-w-xl">{labels.subtitle}</p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CLIENT_SITES.map((site, i) => (
            <Reveal key={site.id} as="div" delay={i * 0.06}>
              <div
                className="group rounded-2xl border border-mist bg-white overflow-hidden
                           hover:border-brand-pink/40 hover:shadow-card
                           transition-[border-color,box-shadow] duration-300"
              >
                {/* Color header */}
                <div className={`relative h-36 bg-gradient-to-br ${site.color} flex items-center justify-center`}>
                  <div className="absolute inset-0 opacity-20"
                    style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.5) 1px, transparent 1px)", backgroundSize: "16px 16px" }} />
                  {/* Browser mockup mini */}
                  <div className="relative bg-white/90 backdrop-blur rounded-lg p-2 w-32 shadow-lg">
                    <div className="flex items-center gap-1 mb-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                      <span className="h-1.5 w-1.5 rounded-full bg-green-400" />
                    </div>
                    <div className="h-1 rounded-full bg-mist mb-1 w-full" />
                    <div className="h-1 rounded-full bg-mist mb-1 w-4/5" />
                    <div className="h-1 rounded-full bg-mist w-3/5" />
                  </div>
                  {/* Category badge */}
                  <span className="absolute top-3 right-3 rounded-full bg-white/90 backdrop-blur text-ink/70 text-[0.6rem] font-bold uppercase tracking-[0.14em] px-2.5 py-1">
                    {site.category}
                  </span>
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="font-display text-h3 font-semibold text-ink">{site.title}</h3>
                  <p className="mt-1 text-xs text-brand-pink font-mono">{site.domain}</p>
                  <p className="mt-2 text-sm text-ink/60 line-clamp-2">{site.desc}</p>
                  <div className="mt-4 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPreview(site)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-ink/15 px-3.5 py-1.5 text-xs font-semibold text-ink hover:border-brand-pink hover:text-brand-pink transition-colors"
                      data-testid={`webdev-preview-${site.id}`}
                    >
                      <Monitor className="h-3.5 w-3.5" />
                      {labels.previewBtn}
                    </button>
                    <a
                      href={site.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-full bg-ink text-cream px-3.5 py-1.5 text-xs font-semibold hover:bg-ink/80 transition-colors"
                    >
                      <ArrowUpRight className="h-3.5 w-3.5" />
                      {labels.openBtn}
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {preview && <SitePreviewModal site={preview} onClose={() => setPreview(null)} />}
      </AnimatePresence>
    </section>
  );
}
